import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { LuCircleCheck, LuTriangleAlert } from 'react-icons/lu'
import { Link } from 'react-router'

import ActionButton from '@/components/misc/ActionButton'
import EmailAddresses from '@/components/misc/EmailAddresses/EmailAddresses'
import PreviewEmail from '@/components/misc/InviteOrRequestReferralPartners/components/PreviewEmail'
import CopyLink from '@/components/misc/PanelInviteEmailTemplate/CopyLink'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Field, FieldLabel } from '@/components/ui/field'
import { Heading } from '@/components/ui/heading'
import { Textarea } from '@/components/ui/textarea'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import useNotyf from '@/hooks/useNotyf'
import { isValidEmail } from '@/lib/helpers/emailAddressHelpers'
import { ROUTES } from '@/lib/routes'
import companyService from '@/lib/services/companyService'
import errorTypeService from '@/lib/services/errorTypeService'
import panelRequestService from '@/lib/services/panelRequestService'
import { ConstraintViolation } from '@/types/api'
import { CreatePanelRequestRequest } from '@/types/requests/panel-requests'

interface Props {
  className?: string
  // Addresses to open the form with. Anything prefilled that isn't a valid
  // address is left in place for the user to fix.
  initialEmails?: string[]
}

interface PartnerRequest {
  email: string
  comment: string
  errorMessages: string[]
  state: 'pending' | 'success' | 'error'
}

const RequestReferralPartners: React.FC<Props> = ({
  className,
  initialEmails = [],
}) => {
  const currentCompany = useCurrentCompanyOrFail()
  const [partnersRequestsMap, setPartnersRequestsMap] = React.useState<
    Map<string, PartnerRequest>
  >(() => buildInitialPartnerRequests(initialEmails))
  const [hasEmailError, setHasEmailError] = React.useState<boolean>(
    initialEmails.some((email) => !isValidEmail(email)),
  )
  const notyf = useNotyf()

  // Collect partner requests into more friendly arrays
  const allPartnersRequests = Array.from(partnersRequestsMap).map(
    ([, partnerRequest]) => partnerRequest,
  )

  const pendingPartnersRequests = allPartnersRequests.filter(
    (partnerRequest) => partnerRequest.state !== 'success',
  )

  const pendingPartnersRequestsEmails: string[] = pendingPartnersRequests.map(
    (partnerRequest) => partnerRequest.email,
  )

  const successfulPartnersRequests = allPartnersRequests.filter(
    (partnerRequest) => partnerRequest.state === 'success',
  )

  const unsuccessfulPartnersRequests = allPartnersRequests.filter(
    (partnerRequest) => partnerRequest.state === 'error',
  )

  const isValid =
    !pendingPartnersRequests.some(
      (partnerRequest) => partnerRequest.comment.length < 3,
    ) &&
    !hasEmailError &&
    pendingPartnersRequests.length > 0

  const shareLink = companyService.getInvitationLink(
    currentCompany.referralCode,
  )

  const createPanelRequestMutation = useMutation({
    mutationFn: (request: CreatePanelRequestRequest) =>
      panelRequestService.createPanelRequest(request),
    onSuccess: (data) => {
      if (data.email) {
        const key = data.email

        setPartnersRequestsMap((prevState) => {
          const newPartners = new Map(prevState)
          const partnerRequest = newPartners.get(key)

          if (partnerRequest != undefined) {
            partnerRequest.state = 'success'
          }

          return newPartners
        })
      }
    },
    onError: async (error, { email }) => {
      if (errorTypeService.isConstraintViolationListError(error) && email) {
        const key = email
        const violationMessages =
          error.response?.data.violations.map(
            (violation: ConstraintViolation): string => violation.message,
          ) ?? []

        if (violationMessages.length > 0) {
          setPartnersRequestsMap((prevState) => {
            const newPartners = new Map(prevState)
            const partnerRequest = newPartners.get(key)
            if (partnerRequest != undefined) {
              partnerRequest.state = 'error'
              partnerRequest.errorMessages = violationMessages
            }
            return newPartners
          })
        }
      } else {
        notyf.error('Failed to send request')
      }
    },
  })

  const handleRequest = async () => {
    const request: CreatePanelRequestRequest = {
      fromCompany: currentCompany['@id'],
      type: 'new_company',
      email: null,
      comment: '',
      toCompany: null,
      toCompanyName: null,
    }

    allPartnersRequests.forEach((partnerRequest) => {
      if (partnerRequest.state !== 'success') {
        createPanelRequestMutation.mutate({
          ...request,
          email: partnerRequest.email,
          comment: partnerRequest.comment,
        })
      }
    })
  }

  return (
    <div className={className}>
      {successfulPartnersRequests.length > 0 && (
        <Alert variant="success" className="fade-in mb-4">
          <LuCircleCheck />

          <AlertTitle>Requests sent</AlertTitle>

          <AlertDescription>
            {successfulPartnersRequests.length > 0 && (
              <ul className="my-2 pl-4">
                {successfulPartnersRequests.map((partnerRequest) => (
                  <li key={partnerRequest.email} className="fade-in">
                    {partnerRequest.email}
                  </li>
                ))}
              </ul>
            )}

            <p className="mb-0">
              Once they have been approved, you will receive a notification. You
              can track the status of your approval requests here:
              <br />
              <Link to={ROUTES.professionalNetworkApprovalRequests()}>
                {`${window.location.protocol}//${window.location.host}${ROUTES.professionalNetworkApprovalRequests()}`}
              </Link>
              .
            </p>
          </AlertDescription>
        </Alert>
      )}

      {unsuccessfulPartnersRequests.length > 0 && (
        <Alert variant="destructive" className="fade-in mb-4">
          <LuTriangleAlert />

          <AlertTitle>These requests couldn't be sent</AlertTitle>

          <AlertDescription>
            <ul className="my-2 pl-4">
              {unsuccessfulPartnersRequests.map((partnerRequest) => (
                <li
                  key={partnerRequest.email}
                  className="fade-in text-color-inherit"
                >
                  <strong className="text-color-inherit">
                    {partnerRequest.email}
                  </strong>
                  {` - ${partnerRequest.errorMessages?.join(' | ')}`}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {successfulPartnersRequests.length > 0 && (
        <Heading size={3}>Send more requests</Heading>
      )}

      <div className="mb-4">
        <EmailAddresses
          emails={pendingPartnersRequestsEmails}
          onChange={(newValues: string[], hasErrors: boolean) => {
            setPartnersRequestsMap((prevState) => {
              const newPartners = new Map<string, PartnerRequest>(prevState)
              // - filter out removed not successful requests
              Array.from(prevState).forEach(([email, partnerRequest]) => {
                if (
                  !newValues.includes(email) &&
                  partnerRequest.state !== 'success'
                ) {
                  newPartners.delete(email)
                }
              })
              // - add new requests
              newValues.forEach((email) => {
                if (!newPartners.has(email)) {
                  newPartners.set(email, {
                    email,
                    comment: '',
                    errorMessages: [],
                    state: 'pending',
                  })
                }
              })
              return newPartners
            })
            setHasEmailError(hasErrors)
          }}
        />
      </div>

      <div className="my-4">
        <span className="font-bold">Please note:</span> Your company has network
        approval enabled. This means an administrator must first approve a firm
        before it can be added to the platform.
      </div>

      {allPartnersRequests.length === 0 && (
        <>
          <Textarea
            className="mb-4"
            rows={7}
            placeholder={`Enter your message to your network approver, explaining why you would like to invite a partner.\r\rConsider including details such as how you know them, whether you have any commercial agreements (e.g., receiving income for referrals), and the reasons for adding them to the network. Providing clear context will help ensure a smooth approval process.`}
            disabled
          />
        </>
      )}

      {allPartnersRequests
        .filter((partnerRequest) => partnerRequest.state != 'success')
        .map((partnerRequest) => (
          <Field key={partnerRequest.email} className="mb-6 gap-1">
            <FieldLabel className="text-[15px] font-medium">
              {allPartnersRequests.length > 1 ? partnerRequest.email : ''}
            </FieldLabel>

            <Textarea
              rows={7}
              value={partnerRequest.comment}
              minLength={3}
              maxLength={1024}
              placeholder={`Enter your message to your network approver, explaining why you would like to invite ${partnerRequest.email}.\r\rConsider including details such as how you know them, whether you have any commercial agreements (e.g., receiving income for referrals), and the reasons for adding them to the network. Providing clear context will help ensure a smooth approval process.`}
              onChange={(e) =>
                setPartnersRequestsMap((prevState) => {
                  const newPartners = new Map(prevState)
                  const newPartnerRequest = newPartners.get(
                    partnerRequest.email,
                  )
                  if (newPartnerRequest) {
                    newPartnerRequest.comment = e.target.value
                  }
                  return newPartners
                })
              }
            />
          </Field>
        ))}

      <div
        className="mt-6 flex flex-wrap items-center justify-between gap-x-4
          gap-y-2"
      >
        <CopyLink
          variant="link"
          textToCopy={shareLink}
          buttonText="Or copy your unique invite link"
          className="px-0!"
        />

        <ActionButton
          isProcessing={createPanelRequestMutation.isPending}
          disabled={!isValid}
          className="w-full sm:w-auto"
          size="lg"
          onClick={handleRequest}
        >
          Send {pendingPartnersRequests.length > 1 ? 'requests' : 'request'}
        </ActionButton>
      </div>

      <div className="mt-4 flex justify-center sm:justify-end">
        <PreviewEmail />
      </div>
    </div>
  )
}

// Seeds the form with addresses the caller already has, each still needing its
// comment before it can be sent.
function buildInitialPartnerRequests(
  emails: string[],
): Map<string, PartnerRequest> {
  return new Map(
    emails.map((email) => [
      email,
      { email, comment: '', errorMessages: [], state: 'pending' as const },
    ]),
  )
}

export default RequestReferralPartners
